import { GetSurveyRuleTargetOptionsRPC } from "@pulse/pulse-rpcs/lib/survey-rules/getSurveyRuleTargetOptionsRPC";
import { SurveyRuleAction } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleAction";
import { Code } from "@pulse/pulse-rpcs/lib/types/code";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import {
  applySnapshot,
  cast,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { getAPIClient } from "../../networking/APIClient";
import { SurveyRuleTargetModel } from "../models/SurveyRuleTargetModel";
import { useGetSurveyRuleTargetOptionsRPCClientImpl } from "../rpcs/RPC";
import { mapToSurveyRuleTargetModel } from "../../utils/SurveyRuleMappers";

interface GetSurveyRuleTargetOptionsStoreInitialState {
  loadingGetSurveyRuleTargetOptions: Record<string, boolean>;
  targetOptions: Record<string, SnapshotIn<typeof SurveyRuleTargetModel>[]>;
}

const getInitialState = (): GetSurveyRuleTargetOptionsStoreInitialState => ({
  loadingGetSurveyRuleTargetOptions: {},
  targetOptions: {},
});

export const GetSurveyRuleTargetOptionsStore = types
  .model("GetSurveyRuleTargetOptionsStore", {
    loadingGetSurveyRuleTargetOptions: types.map(types.boolean),
    targetOptions: types.map(types.array(SurveyRuleTargetModel)),
  })
  .actions((store) => ({
    getSurveyRuleTargetOptions: flow(function* (
      surveyId: string,
      action: SurveyRuleAction.SurveyRuleAction,
      questionCode: string | null,
      requestKey?: string,
    ) {
      const optionsKey = requestKey ?? "default";
      try {
        store.targetOptions.clear();
        store.targetOptions.delete(optionsKey);
        store.loadingGetSurveyRuleTargetOptions.set(optionsKey, true);

        const apiClient = getAPIClient(store);
        const request = new GetSurveyRuleTargetOptionsRPC.Request(
          new LeoUUID(surveyId),
          questionCode ? new Code(questionCode) : null,
          action,
        );

        const result: LeoRPCResult<
          GetSurveyRuleTargetOptionsRPC.Response,
          GetSurveyRuleTargetOptionsRPC.Errors.Errors
        > =
          yield useGetSurveyRuleTargetOptionsRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          const targetOptions = result.response.targets.map(
            mapToSurveyRuleTargetModel,
          );
          store.targetOptions.set(optionsKey, cast(targetOptions));
          return;
        }

        switch (result.error.code) {
          case GetSurveyRuleTargetOptionsRPC.RPCError.InvalidSurveyId:
          case GetSurveyRuleTargetOptionsRPC.RPCError.InvalidQuestionCode:
          case GetSurveyRuleTargetOptionsRPC.RPCError.UnsupportedQuestionType:
            return result.error.code;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingGetSurveyRuleTargetOptions.delete(optionsKey);
      }
    }),

    resetStore: (): void => {
      applySnapshot(store, getInitialState());
    },
  }));

export const createGetSurveyRuleTargetOptionsStore = (): Instance<
  typeof GetSurveyRuleTargetOptionsStore
> => GetSurveyRuleTargetOptionsStore.create(getInitialState());
